// src/api/mainApi.ts
/**
 * Main API client
 * - Matches /api/v1/main controller DTOs & endpoints
 * - Uses native fetch with small helper (no global axios dependency)
 */

const BASE = '/api/main';

/* ============================== Types ============================== */
/** 상단 섹터 탭 */
export type SectorTab = {
    key: string;   // "chip"
    label: string; // "Semiconductor/IT"
    order: number; // 정렬 우선순위
};

/** 히어로/요약 */
export type LatestDigest = {
    savedCount: number;                // 오늘 저장 건수
    minFeedId: number | null;
    maxFeedId: number | null;
    sectorCounts: Record<string, number>; // { "chip": 5, "energy": 3, ... }
    generatedAtKST: string;              // "YYYY-MM-DD HH:mm"
};

/** 기능 카드 */
export type FeatureMetricItem = {
    title: string;
    desc: string;
    value: number; // 0~100
};
export type FeatureMetricGroup = {
    groupTitle: string;            // "요약" | "분석" | "시그널"
    items: FeatureMetricItem[];
};

/** 반응 메트릭 */
export type ReactionMetrics = {
    blendedAlpha: number | null;                 // profile 가중·감쇠 알파
    profileUsed: Record<string, unknown> | null; // { weights, decay, windows }
    meta: Record<string, unknown> | null;        // { basket_size, offsets, eff_weight_sum }
};

/** 섹터별 시그널 카드 */
export type SignalCard = {
    feedId: number;
    title: string | null;
    url: string | null;
    sector: string;
    publishedAt: string | null; // "YYYY-MM-DD HH:mm"
    impactScore: number | null;
    interpretationKr: string | null; // 한글 요약
    reaction: ReactionMetrics | null;
};

/** 섹터 반응 프로파일 */
export type ReactionProfileDTO = {
    windows: number[];                    // [1,3,7]
    weights: Record<number, number>;      // {1:0.25,3:0.45,7:0.30}
    decay: Record<number, number>;        // {1:0.85,3:1.00,7:1.05}
};

/** per-window 성과 */
export type WindowStat = {
    retWeighted: number | null; // 이벤트 바스켓 가중수익
    retBaseline: number | null; // 섹터 기준선 수익
    alpha: number | null;       // 초과수익
    sample: number;             // 표본 수
    confidence: string;         // "낮음" | "중간" | "높음"
};
export type ReactionWindowsDTO = {
    windows: Record<number, WindowStat>; // {1:{...},3:{...},7:{...}}
    blendedAlpha: number | null;         // 섹터 프로파일 가중·감쇠 적용 알파
    profileUsed: ReactionProfileDTO;
    meta: Record<string, unknown>;
};

/** 인사이트 목록행 (정책 요약 전체 보기) */
export type InsightRow = {
    feedId: number;
    title: string | null;
    sector: string;
    publishedAt: string | null;
    impactScore: number | null;
    sourceName: string | null;
};

/* ============================== Helpers ============================== */

function qs(params?: Record<string, unknown>): string {
    if (!params) return '';
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        sp.set(k, String(v));
    });
    const s = sp.toString();
    return s ? `?${s}` : '';
}

async function getJSON<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    const res = await fetch(`${BASE}${path}${qs(params)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
    });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`GET ${path} failed: ${res.status} ${res.statusText} ${text ? `- ${text}` : ''}`);
    }
    return res.json() as Promise<T>;
}

/* ============================== API ============================== */

/** 헬스체크 (옵션) */
export async function health(): Promise<{ ok: boolean; service: string; ts: string }> {
    return getJSON('/health');
}

/** 상단 섹터 탭 */
export async function listSectorTabs(): Promise<SectorTab[]> {
    return getJSON<SectorTab[]>('/sector-tabs');
}

/** 최신 집계 요약 */
export async function getLatestDigest(): Promise<LatestDigest> {
    return getJSON<LatestDigest>('/latest-digest');
}

/** 기능 지표 그룹 */
export async function getFeatureMetrics(): Promise<FeatureMetricGroup[]> {
    return getJSON<FeatureMetricGroup[]>('/feature-metrics');
}

/** 섹터별 최근 시그널 카드 목록 */
export async function listSectorSignals(params: {
    sector: string;               // ex) "chip", "bio_healthcare"
    limit?: number;               // default 10
    offset?: number;              // default 0
}): Promise<SignalCard[]> {
    const { sector, limit = 10, offset = 0 } = params || ({} as any);
    return getJSON<SignalCard[]>('/sector-signals', { sector, limit, offset });
}

/** 섹터 반응 프로파일 */
export async function getReactionProfile(sector: string): Promise<ReactionProfileDTO> {
    return getJSON<ReactionProfileDTO>('/reaction-profile', { sector });
}

/** 개별 feed의 per-window 성과 */
export async function getReactionWindows(feedId: number | string): Promise<ReactionWindowsDTO> {
    return getJSON<ReactionWindowsDTO>('/reaction-windows', { feedId });
}

/** “정책 요약 전체 보기” 리스트 */
export async function listInsights(params?: {
    limit?: number;  // default 20
    offset?: number; // default 0
}): Promise<InsightRow[]> {
    const limit = params?.limit ?? 20;
    const offset = params?.offset ?? 0;
    return getJSON<InsightRow[]>('/insight-list', { limit, offset });
}

/* ============================== Namespaced export (선호시) ============================== */
const MainApi = {
    health,
    listSectorTabs,
    getLatestDigest,
    getFeatureMetrics,
    listSectorSignals,
    getReactionProfile,
    getReactionWindows,
    listInsights,
};
export default MainApi;
